import APIProductService from "./APIProductService";
import APIArticleService from "./BlogService";
import FAQService from "./FAQService";
import { productLink } from "../utils/resolveProductFamily";

// Search results show a short plain-text snippet; FAQ answers and article bodies are
// Markdown/HTML, so drop the markup before cutting (otherwise "**(PSD2)**" shows as-is).
const plainSnippet = (text, length = 200) =>
  text
    ? text
        .replace(/<[^>]*>/g, " ")
        .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/(\*\*|__|\*|`)/g, "")
        .replace(/^\s{0,3}(#{1,6}|>|[-+]|\d+\.)\s+/gm, "")
        .replace(/\s+/g, " ")
        .trim()
        .substring(0, length)
    : text;

/**
 * SearchService - Unified search service that integrates all content services
 *
 * This service provides centralized search functionality across all content types:
 * - Articles (from APIArticleService)
 * - Products (from APIProductService)
 * - APIs (from APIProductService)
 * - FAQs (from FAQService)
 */
class SearchService {
  constructor() {
    this.searchCache = new Map();
    this.allContentCache = null;
    this.lastFetchTime = null;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Main search function that aggregates results from all services
   * @param {string} searchTerm - Search query
   * @param {string} type - Filter by type: 'article', 'api_product', 'api_endpoint', or null for all
   * @param {number} page - Not used anymore, kept for compatibility
   * @param {number} itemsPerPage - Not used anymore, kept for compatibility
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token
   * @returns {Promise<Object>} Search results with items and metadata
   */
  async search(
    searchTerm,
    type,
    page = 0,
    itemsPerPage = 10,
    userId,
    csrfToken
  ) {
    try {
      // Get all content first (with caching)
      const allContent = await this.getAllContent(userId, csrfToken);

      // Filter by search term
      let filteredContent = this.filterBySearchTerm(allContent, searchTerm);

      // Filter by type if specified
      if (type) {
        filteredContent = filteredContent.filter((item) => item.type === type);
      }

      // Highlight search term in results if present
      const highlightedContent = searchTerm
        ? filteredContent.map((item) =>
            this.highlightSearchTerm(item, searchTerm)
          )
        : filteredContent;

      return {
        items: highlightedContent,
        meta: {
          total: filteredContent.length,
        },
      };
    } catch (error) {
      console.error("SearchService error:", error);
      return {
        items: [],
        meta: {
          total: 0,
        },
      };
    }
  }

  /**
   * Get all content from all services with caching
   * @private
   */
  async getAllContent(userId, csrfToken) {
    // Check if cache is still valid
    if (this.allContentCache && this.lastFetchTime) {
      const cacheAge = Date.now() - this.lastFetchTime;
      if (cacheAge < this.cacheExpiry) {
        return this.allContentCache;
      }
    }

    const allContent = [];

    // Fetch all content in parallel for better performance
    const [articles, blogArticles, products, faqs] = await Promise.allSettled([
      this.getArticles(userId),
      this.getBlogArticles(userId),
      this.getProducts(userId, csrfToken),
      this.getFaqs(userId, csrfToken),
    ]);

    // Process articles from APIArticleService
    if (articles.status === "fulfilled" && articles.value) {
      const articleItems = this.processArticles(articles.value);
      allContent.push(...articleItems);
    }

    // Process blog articles
    if (blogArticles.status === "fulfilled" && blogArticles.value) {
      const blogItems = this.processBlogArticles(blogArticles.value);
      allContent.push(...blogItems);
    }

    // Process products and APIs
    if (products.status === "fulfilled" && products.value) {
      const { productItems, apiItems } = this.processProducts(products.value);
      allContent.push(...productItems);
      allContent.push(...apiItems);
    }

    // Process FAQs
    if (faqs.status === "fulfilled" && faqs.value) {
      const faqItems = this.processFaqs(faqs.value);
      allContent.push(...faqItems);
    }

    // Update cache
    this.allContentCache = allContent;
    this.lastFetchTime = Date.now();

    return allContent;
  }

  /**
   * Fetch articles from APIArticleService
   * @private
   */
  async getArticles(userId) {
    try {
      const result = await APIArticleService.searchArticles(
        "",
        [],
        0,
        1000,
        userId
      );
      return result.articles || [];
    } catch (error) {
      console.error("Error fetching articles:", error);
      return [];
    }
  }

  /**
   * Fetch blog articles from APIArticleService (formerly BlogService)
   * @private
   */
  async getBlogArticles(userId) {
    try {
      // Set up APIArticleService if not already configured (BlogService compatibility)
      if (!APIArticleService.application) {
        APIArticleService.setApplication(
          "03FC0B90-DFAD-11EE-8D86-0800200C9A66"
        );
      }
      if (!APIArticleService.userId) {
        APIArticleService.setUserId(
          userId || "00000000-0000-0000-0000-000000000000"
        );
      }

      const catalogue = await APIArticleService.getCatalogue();
      return catalogue || [];
    } catch (error) {
      console.error("Error fetching blog articles:", error);
      return [];
    }
  }

  /**
   * Fetch products from APIProductService
   * @private
   */
  async getProducts(userId, csrfToken) {
    try {
      const products = await APIProductService.getProductList(
        userId,
        csrfToken
      );
      return products || [];
    } catch (error) {
      console.error("Error fetching products:", error);
      return [];
    }
  }

  /**
   * Fetch FAQs from FAQService
   * @private
   */
  async getFaqs(userId, csrfToken) {
    try {
      const result = await FAQService.searchFaqs("", "", userId, csrfToken);
      return result?.payload?.faqs || [];
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      return [];
    }
  }

  /**
   * Process articles into search format
   * @private
   */
  processArticles(articles) {
    return articles.map((article) => ({
      uuid: article.uuid || article.id,
      title: article.title,
      description: plainSnippet(article.summary || article.body),
      link: `/news/${
        article.url || article.fileName || article.uuid || article.id
      }`,
      type: "article",
      created: article.created || article.date,
      imageUrl: article.imageUrl,
      categories: article.categories || [],
    }));
  }

  /**
   * Process blog articles into search format
   * @private
   */
  processBlogArticles(articles) {
    return articles.map((article) => ({
      uuid: article.id || article.url,
      title: article.title,
      description: plainSnippet(article.description || article.summary),
      link: `/news/${article.url || article.id}`,
      type: "article",
      created: article.datePublished || article.created,
      author: article.author,
      categories: article.tags || [],
    }));
  }

  /**
   * Process products into product and API search items
   * @private
   */
  processProducts(products) {
    const productItems = [];
    const apiItems = [];

    products.forEach((product) => {
      // Add product item
      productItems.push({
        uuid: product.uuid || product.id,
        title: product.title || product.productTitle || product.productName,
        description: product.description || product.productDescription,
        link:
          product.link ||
          productLink(
            product.alias || product.urlAlias || product.UrlAlias,
            product.deprecated
          ) ||
          `/apiProduct/${product.uuid || product.id}`,
        type: "api_product",
        deprecated: product.deprecated,
        imageUrl: product.imageUrl || product.productImage,
        created: product.created,
      });

      // Note: API endpoints are not included in the product list response
      // They are only available when fetching individual product details
      // To include APIs in search, we would need to fetch details for each product
      // which would be too many API calls

      // Add API items from product if available (for future when API includes them)
      if (product.apiEndpoints && Array.isArray(product.apiEndpoints)) {
        product.apiEndpoints.forEach((api) => {
          apiItems.push({
            uuid: api.uuid || api.id,
            title: api.title || api.apiTitle || api.name,
            description: api.description || api.apiDescription,
            link: api.link || `/documentation/${api.uuid || api.id}`,
            type: "api_endpoint",
            deprecated: api.deprecated,
            version: api.version || api.apiVersion,
            productTitle:
              product.title || product.productTitle || product.productName,
            created: api.created,
          });
        });
      }
    });

    return { productItems, apiItems };
  }

  /**
   * Process FAQs into search format
   * @private
   */
  processFaqs(faqs) {
    return faqs.map((faq, index) => ({
      uuid: faq.id || faq.uuid || `faq-${index}`,
      title: faq.question,
      description: plainSnippet(faq.answer),
      link: `/faq-page?search=${encodeURIComponent(faq.question)}`, // Link with search query
      type: "faq", // FAQs have their own type
      created: faq.created,
      categories: faq.faqCategories || [],
    }));
  }

  /**
   * Filter content by search term
   * @private
   */
  filterBySearchTerm(content, searchTerm) {
    if (!searchTerm || searchTerm.trim() === "") {
      return content;
    }

    const searchLower = searchTerm.toLowerCase();

    return content.filter((item) => {
      const titleMatch = item.title?.toLowerCase().includes(searchLower);
      const descriptionMatch = item.description
        ?.toLowerCase()
        .includes(searchLower);
      const categoryMatch = item.categories?.some((cat) =>
        (typeof cat === "string" ? cat : cat.name)
          ?.toLowerCase()
          .includes(searchLower)
      );

      return titleMatch || descriptionMatch || categoryMatch;
    });
  }

  /**
   * Highlight search term in item fields
   * @private
   */
  highlightSearchTerm(item, searchTerm) {
    if (!searchTerm) return item;

    const regex = new RegExp(`(${searchTerm})`, "gi");

    const highlightedItem = { ...item };

    // Highlight in title
    if (highlightedItem.title) {
      highlightedItem.title = highlightedItem.title.replace(
        regex,
        '<span class="highlight">$1</span>'
      );
    }

    return highlightedItem;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.searchCache.clear();
    this.allContentCache = null;
    this.lastFetchTime = null;
  }

  /**
   * Get cached search results
   * @param {string} searchTerm - Search term
   * @param {string} type - Content type filter
   * @param {number} page - Page number
   * @param {number} itemsPerPage - Items per page
   * @returns {Object|null} Cached results or null
   */
  getCachedSearch(searchTerm, type, page, itemsPerPage) {
    const cacheKey = `${searchTerm}_${type}_${page}_${itemsPerPage}`;
    return this.searchCache.get(cacheKey) || null;
  }
}

export default new SearchService();
